/**
 * AdvancedTab Component
 *
 * Advanced settings tab containing:
 * - Message processing settings
 * - Session/cache settings
 * - First message hook configuration
 */

import React, { useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { FieldSection } from '@/components/fields/FieldSection';
import { FIELD_CONFIG } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import type { AgentConfiguration } from '@/types';

// Advanced configuration sections
const ADVANCED_SECTIONS = [
  'message_processing',
  'session_summary',
  'advanced',
  'first_message',
];

interface AdvancedTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const AdvancedTab: React.FC<AdvancedTabProps> = ({
  config: _config, // Kept for interface consistency
  onChange,
  appContext,
}) => {
  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (ADVANCED_SECTIONS.includes(section)) {
        if (!groups[section]) {
          groups[section] = [];
        }
        groups[section].push({ name, schema });
      }
    });

    return groups;
  }, []);

  // Check if a section has any visible fields
  const sectionHasVisibleFields = useCallback((sectionKey: string) => {
    const fields = groupedFields[sectionKey];
    if (!fields || fields.length === 0) return false;

    return fields.some((field) => {
      if (!field.schema.showIf) return true;
      return field.schema.showIf(appContext);
    });
  }, [groupedFields, appContext]);

  // Sort sections by ADVANCED_SECTIONS order
  const sortedSections = useMemo(() => {
    return ADVANCED_SECTIONS
      .filter(key => FIELD_SECTIONS[key] && groupedFields[key]?.length > 0)
      .map(key => [key, FIELD_SECTIONS[key]] as [string, typeof FIELD_SECTIONS[string]]);
  }, [groupedFields]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">高级设置</h2>
          <p className="text-sm text-slate-500 mt-1">
            消息处理、会话管理和其他高级选项
          </p>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sortedSections.map(([sectionKey, sectionConfig]) => {
          if (!sectionHasVisibleFields(sectionKey)) {
            return null;
          }
          return (
            <div
              key={sectionKey}
              className={sectionConfig.layout === 1 ? 'xl:col-span-2' : ''}
            >
              <FieldSection
                sectionKey={sectionKey}
                sectionConfig={sectionConfig}
                fields={groupedFields[sectionKey] || []}
                values={appContext}
                onChange={onChange}
                appContext={appContext}
              />
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-2">高级设置说明</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• <strong>History Settings</strong>: 控制对话历史如何影响响应上下文</li>
          <li>• <strong>Session Summary</strong>: 自动生成会话摘要，为长对话提供压缩的上下文</li>
          <li>• <strong>Session Timeout</strong>: 会话不活动后的过期时间</li>
          <li>• <strong>Resource Cache</strong>: 控制Agent资源缓存行为</li>
          <li>• <strong>First Message</strong>: 新会话首条消息的特殊指令</li>
        </ul>
      </Card>
    </div>
  );
};

export default AdvancedTab;
